// react
import * as React from "react";
import { connect } from "react-redux";
import { useTranslation } from 'react-i18next';
import { Button, Spinner } from "@fluentui/react-components";
import { Delete16Regular, DocumentBulletListRegular, CheckmarkCircle16Filled, DismissCircle16Filled } from "@fluentui/react-icons";

//styles
import "./VisitFormConceptPayable.Styles.css";

// component functions
import * as ComponentFunctions from "./VisitFormConceptPayable.Functions";

// models
import { ContractConceptRelationship } from "../../../models/Entities/Contracts/ContractConceptRelationship";
import ConfirmDialog from "../../../commons/component/ConfirmDialog/ConfirmDialog";


const VisitFormConceptPayableListComponent = (props: ComponentFunctions.Props) => {

    const { t } = useTranslation();

    React.useEffect(() => {
        if (!props.LoadingSuccess) {
            props.getAllContractConceptRelationships(props.ContractId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.ContractId]);

    const [deletingId, setDeletingId] = React.useState<number | undefined>(undefined);
    const [confirmOpen, setConfirmOpen] = React.useState<boolean>(false);
    const [pendingDeleteId, setPendingDeleteId] = React.useState<number | undefined>(undefined);
    const [pendingDeleteItem, setPendingDeleteItem] = React.useState<ContractConceptRelationship | undefined>(undefined);

    React.useEffect(() => {
        if (props.successDeleting) {
            props.getAllContractConceptRelationships(props.ContractId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.successDeleting, props.ContractId]);

    React.useEffect(() => {
        if (!props.isDeleting) {
            setDeletingId(undefined);
        }
    }, [props.isDeleting]);

    const concepts = Array.isArray(props.concepts) ? props.concepts : [];

    const uniqueConcepts = React.useMemo(() => {
        const map = new Map<string, ContractConceptRelationship>();
        for (const c of concepts) {
            const key = (c.formConcept?.toLowerCase() ?? '').trim();
            if (!map.has(key)) {
                map.set(key, c);
            }
        }
        return Array.from(map.values());
    }, [concepts]);

    const payableCount = uniqueConcepts.filter(c => c.isPayable === true).length;
    const notPayableCount = uniqueConcepts.length - payableCount;

    return (
        <div className="vfcp-container">
            <div className="vfcp-header">
                <div className="vfcp-header-titleBlock">
                    <span className="vfcp-header-eyebrow">
                        {t('contractAssociation.visitFormConceptPayableList.eyebrow', 'Conceptos del formulario')}
                    </span>
                    <span className="vfcp-header-title">
                        {uniqueConcepts.length}{' '}
                        <span className="vfcp-header-titleSuffix">
                            {uniqueConcepts.length === 1
                                ? t('contractAssociation.visitFormConceptPayableList.itemSingular', 'concepto')
                                : t('contractAssociation.visitFormConceptPayableList.itemPlural', 'conceptos')}
                        </span>
                    </span>
                </div>
                {uniqueConcepts.length > 0 && (
                    <div className="vfcp-header-stats">
                        <span className="vfcp-stat vfcp-stat--payable" title={t('contractAssociation.visitFormConceptPayableList.payable', 'Liquidables')}>
                            <CheckmarkCircle16Filled />
                            <span className="vfcp-stat-value">{payableCount}</span>
                            <span className="vfcp-stat-label">{t('contractAssociation.visitFormConceptPayableList.payable', 'Liquidables')}</span>
                        </span>
                        <span className="vfcp-stat vfcp-stat--notPayable" title={t('contractAssociation.visitFormConceptPayableList.notPayable', 'No liquidables')}>
                            <DismissCircle16Filled />
                            <span className="vfcp-stat-value">{notPayableCount}</span>
                            <span className="vfcp-stat-label">{t('contractAssociation.visitFormConceptPayableList.notPayable', 'No liquidables')}</span>
                        </span>
                    </div>
                )}
            </div>

            {props.isLoading && !props.LoadingSuccess ? (
                <div className="vfcp-state vfcp-state--loading">
                    <Spinner size="tiny" />
                    <span>{t('common.loading', 'Cargando...')}</span>
                </div>
            ) : uniqueConcepts.length === 0 ? (
                <div className="vfcp-state vfcp-state--empty">
                    <DocumentBulletListRegular />
                    <span>{t('contractAssociation.visitFormConceptPayableList.empty', 'Sin conceptos asociados.')}</span>
                </div>
            ) : (
                <ul className="VisitFormConceptPayableList">
                    {uniqueConcepts.map((concept: ContractConceptRelationship) => {
                        const isRowDeleting = props.isDeleting && deletingId === concept.id;
                        return (
                            <li
                                key={concept.id ?? `${concept.formConcept}-${concept.isPayable ? '1' : '0'}`}
                                className={`vfcp-row ${concept.isPayable ? 'is-payable' : 'is-notPayable'} ${isRowDeleting ? 'is-deleting' : ''}`}
                            >
                                <span className="vfcp-row-iconChip" aria-hidden="true">
                                    <DocumentBulletListRegular />
                                </span>
                                <span className="vfcp-row-main">
                                    <span className="vfcp-row-name">{concept.formConcept}</span>
                                    <span className={`vfcp-row-status ${concept.isPayable ? 'is-payable' : 'is-notPayable'}`}>
                                        {concept.isPayable
                                            ? <><CheckmarkCircle16Filled /> {t('contractAssociation.visitFormConceptPayableList.payable', 'Liquidable')}</>
                                            : <><DismissCircle16Filled /> {t('contractAssociation.visitFormConceptPayableList.notPayable', 'No liquidable')}</>}
                                    </span>
                                </span>
                                <Button
                                    appearance="subtle"
                                    size="small"
                                    className="vfcp-row-delete"
                                    title={t('common.delete', 'Eliminar')}
                                    icon={isRowDeleting ? <Spinner size="extra-tiny" /> : <Delete16Regular />}
                                    disabled={isRowDeleting}
                                    onClick={() => {
                                        setPendingDeleteId(concept.id);
                                        setPendingDeleteItem(concept);
                                        setConfirmOpen(true);
                                    }}
                                />
                            </li>
                        );
                    })}
                </ul>
            )}

            <ConfirmDialog
                open={confirmOpen}
                title={t('contractAssociation.visitFormConceptPayableList.confirmDeleteTitle', 'Confirm deletion')}
                message={t('contractAssociation.visitFormConceptPayableList.confirmDeleteItemLabel', 'You are about to delete the item')}
                highlight={pendingDeleteItem ? pendingDeleteItem.formConcept : undefined}
                confirmText={t('common.delete', 'Delete')}
                cancelText={t('common.cancel', 'Cancel')}
                onCancel={() => { setConfirmOpen(false); setPendingDeleteId(undefined); setPendingDeleteItem(undefined); }}
                onConfirm={() => {
                    if (pendingDeleteId !== undefined) {
                        setConfirmOpen(false);
                        setDeletingId(pendingDeleteId);
                        props.deleteContractConceptRelationship?.(pendingDeleteId);
                    }
                }}
            />
        </div>
    );

}

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(VisitFormConceptPayableListComponent as any);